/**
 * @fileoverview Template: the content frame every page renders inside.
 * @module components/templates/PageTemplate
 *
 * @description
 * The brand (logo + app name) lives once in {@link AppHeader}, rendered by
 * the app shell above every route; this template only owns the scrollable
 * content area below it; see `.page__content`'s scroll fallback note.
 */

import type { ReactNode } from "react"

import "./PageTemplate.css"

/** Props accepted by {@link PageTemplate}. */
export interface PageTemplateProps {
	/** Page heading, kept for context and assistive technology. */
	readonly title: string
	/** Page content. */
	readonly children: ReactNode
}

/**
 * Full-height page frame: a small heading over a content area that scrolls
 * internally rather than the whole page.
 *
 * @description
 * `.page__content` keeps a scroll fallback for the rare viewport too short
 * to fit everything (a large accessibility zoom, a tiny landscape phone) —
 * the target is scroll-free, not scroll-proof.
 *
 * @param props - Title and content.
 * @returns The rendered page shell.
 */
export function PageTemplate({ title, children }: PageTemplateProps) {
	return (
		<main className="page">
			<h1 className="page__title">{title}</h1>
			<div className="page__content">{children}</div>
		</main>
	)
}
